//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import * as galleriesApi from '@apis/galleries/galleriesApi';
import { galleryPickerActions } from '@galleries/actions/galleriesPickerActions';
import { GALLERIES_ACTIONS } from '@galleries/constants/galleriesConstants';
import GalleryContainerId from '@galleries/models/GalleryContainerId';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { getSelectedGallery } from '@galleries/selectors/getSelectedGallery';
import { ICuralateStore } from '@models/AppState';
import { createAction } from '@utils/actions';
import { Dispatch } from 'redux';

function openModal() {
  return createAction({
    type: GALLERIES_ACTIONS.OPEN_DELETE_GALLERY_MODAL,
  });
}

function closeModal() {
  return createAction({
    type: GALLERIES_ACTIONS.CLOSE_DELETE_GALLERY_MODAL,
  });
}

function requestDeleteGallery() {
  return createAction({
    type: GALLERIES_ACTIONS.REQUEST_DELETE_GALLERY,
  });
}

function receiveDeleteGallerySuccess() {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_DELETE_GALLERY_SUCCEEDED,
  });
}

function receiveDeleteGalleryFailed(error: string) {
  return createAction({
    type: GALLERIES_ACTIONS.RECEIVE_DELETE_GALLERY_FAILED,
    error,
  });
}

function clearDeleteGalleryError() {
  return createAction({
    type: GALLERIES_ACTIONS.CLEAR_DELETE_GALLERY_ERROR,
  });
}

function setDeleteGalleryName(deleteGalleryName: string) {
  return createAction({
    type: GALLERIES_ACTIONS.SET_DELETE_GALLERY_NAME,
    deleteGalleryName,
  });
}

export type GalleriesDeleteGalleryActionType = ReturnType<
  | typeof openModal
  | typeof closeModal
  | typeof requestDeleteGallery
  | typeof receiveDeleteGallerySuccess
  | typeof receiveDeleteGalleryFailed
  | typeof clearDeleteGalleryError
  | typeof setDeleteGalleryName
>;

export const deleteGalleryActions = {
  closeModal,
  openModal,
  setDeleteGalleryName,

  deleteGallery:
    (galleryContainerId?: GalleryContainerId) =>
    async (
      dispatch: Dispatch,
      getState: () => ICuralateStore,
    ): Promise<void> => {
      const { errors, isDeletingGallery } =
        getState().galleries.deleteGalleryModal;

      if (isDeletingGallery) {
        return;
      }

      if (errors.deleteGalleryError) {
        dispatch(clearDeleteGalleryError());
      }

      try {
        dispatch(requestDeleteGallery());

        // Delete the gallery
        const containerId =
          galleryContainerId ?? getSelectedGallery(getState()).containerId;
        await galleriesApi.deleteGallery(containerId);

        // Refetch gallery data after the delete has completed
        await dispatch(galleriesDataActions.getGalleries());

        // Select the default gallery
        dispatch(galleryPickerActions.selectDefaultGallery());

        dispatch(receiveDeleteGallerySuccess());
      } catch (e) {
        dispatch(
          receiveDeleteGalleryFailed('An error occurred. Please try again'),
        );
      }
    },
};
